import { useEffect, useState } from 'react'
import Icon from './Icon'
import { listTrustedDevices, revokeTrustedDevice } from '../api/security'
import { deviceLabel } from '../utils/deviceLabel'
import styles from './TrustedDevicesModal.module.css'

// Same mapping AdminUserRow.jsx / UserMenu.jsx use for MFA method display.
const MFA_DISPLAY = {
  totp: { icon: 'lock', label: 'Authenticator app' },
  email: { icon: 'mail', label: 'Email code' },
}

function formatDate(isoString) {
  if (!isoString) return '—'
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

/**
 * TrustedDevicesModal — "Trust this device" grants the user picked up at MFA
 * verify time (routes/mfa.py), self-service: any signed-in user can see and
 * revoke their own here (not admin-gated — these belong to the caller, same
 * scoping the backend enforces via useruid on every devices call). Opened
 * from UserMenu's account popover, only reachable once the caller is
 * actually enrolled (the button that opens this is itself gated on that).
 *
 * Props:
 *   mfaMethod {'totp'|'email'|null} — shown as a status line at the top
 *   onClose   {function}
 */
export default function TrustedDevicesModal({ mfaMethod, onClose }) {
  const [devices, setDevices] = useState(null)
  const [error, setError] = useState(null)
  const [revokingId, setRevokingId] = useState(null)

  useEffect(() => {
    let cancelled = false
    listTrustedDevices()
      .then(data => { if (!cancelled) setDevices(data) })
      .catch(err => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  async function handleForget(deviceId) {
    setRevokingId(deviceId)
    setError(null)
    try {
      await revokeTrustedDevice(deviceId)
      setDevices(prev => prev.filter(d => d.id !== deviceId))
    } catch (err) {
      setError(err.message)
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Trusted devices">
        <div className={styles.header}>
          <h2 className={styles.title}>Trusted devices</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>

        {MFA_DISPLAY[mfaMethod] && (
          <div className={styles.mfaStatus}>
            <span className={styles.mfaStatusIcon}><Icon name={MFA_DISPLAY[mfaMethod].icon} width="14" height="14" /></span>
            Two-factor authentication is on — {MFA_DISPLAY[mfaMethod].label}
          </div>
        )}

        <p className={styles.subtext}>
          Devices you've chosen to trust skip the two-factor prompt until their trust period
          expires. Forget a device to require the code again next time it signs in.
        </p>

        {error && <p className={styles.error}>{error}</p>}

        {devices === null && !error && <p className={styles.loading}>Loading…</p>}

        {devices?.length === 0 && (
          <p className={styles.empty}>No trusted devices — every login on this account currently asks for a code.</p>
        )}

        {devices && devices.length > 0 && (
          <div className={styles.list}>
            {devices.map(d => (
              <div key={d.id} className={styles.row}>
                <div className={styles.rowInfo}>
                  <span className={styles.rowDevice}>{deviceLabel(d.user_agent)}</span>
                  <span className={styles.rowMeta}>Last used {formatDate(d.last_used_at)} · Expires {formatDate(d.expires_at)}</span>
                </div>
                <button
                  type="button"
                  className={styles.forgetBtn}
                  onClick={() => handleForget(d.id)}
                  disabled={revokingId === d.id}
                >
                  {revokingId === d.id ? 'Forgetting…' : 'Forget'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
